import { useState } from "react";
import { IconButton } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import ArrowForward from "@mui/icons-material/ArrowForward";

import KogInputTextLogin from "@/main/components/shared/inputText/inputTextLogin";
import { LoginFormProvider, useLoginFormContext } from "@/main/hooks/loginHook";
import { useStyles, ContainerToggle, ButtonLogin, ButtonToggleScreen } from "../../styles";
import * as S from "./styles";
import { useTheme } from "@mui/material/styles";
import { getTranslateByKey } from "@/main/services/utils";
import { getSecondaryColors } from "@/main/style/styleMaterialUIGlobal";
import { KeyValue } from "@/main/models/common";
import LoginUserLogged from "../LoginUserLogged/loginUserLogged";
import { SpinnerLoading } from "@/main/components/shared/styles";

interface ILoginAreaProps {
	toggleRecoveryPassword: () => void;
	translatesLocal: KeyValue[];
}

function LoginArea({ toggleRecoveryPassword, translatesLocal }: ILoginAreaProps) {
	const styles = useStyles();
	const theme = useTheme();
	const secondaryColors = getSecondaryColors(theme);
	const loginForm = useLoginFormContext();
	const { serverErrors, errors, hasUserLogged, submitInProgress } = loginForm;
	const [showPassword, setShowPassword] = useState(false);
	const handleClickShowPassword = () => setShowPassword(!showPassword);

	if (hasUserLogged) return <LoginUserLogged translatesLocal={translatesLocal} />;

	return (
		<S.Container>
			<S.ContainerForm id="login-form" onSubmit={loginForm.submit}>
				<KogInputTextLogin
					autoFocus
					style={{ marginBottom: "10px" }}
					id="txt-login-email"
					variant="outlined"
					type="text"
					label={getTranslateByKey("information.userName", false, translatesLocal)}
					error={!!errors.username || !!serverErrors}
					helperText={errors.username?.message}
					className={styles.inputTextCustom}
					inputProps={{ maxLength: 100 }}
					title={getTranslateByKey("information.userName", false, translatesLocal)}
					{...loginForm.register("username", {
						required: getTranslateByKey("information.usernameRequired", true, translatesLocal),
					})}
				/>
				<KogInputTextLogin
					id="txt-login-password"
					variant="outlined"
					label={getTranslateByKey("information.password", false, translatesLocal)}
					positionIcon="end"
					error={!!errors.password || !!serverErrors}
					helperText={errors.password?.message}
					type={showPassword ? "text" : "password"}
					className={styles.inputTextCustom}
					inputProps={{ maxLength: 100 }}
					icon={
						<IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} edge="end">
							{showPassword ? (
								<VisibilityOutlinedIcon style={{ color: secondaryColors.contrastText }} />
							) : (
								<VisibilityOffOutlinedIcon style={{ color: secondaryColors.contrastText }} />
							)}
						</IconButton>
					}
					title={getTranslateByKey("information.password", false, translatesLocal)}
					{...loginForm.register("password", {
						required: getTranslateByKey("information.passwordRequired", true, translatesLocal),
					})}
				/>

				<div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
					{serverErrors &&
						serverErrors.map((item) => (
							<div
								key={item}
								style={{
									display: "flex",
									alignItems: "flex-start",
									width: "100%",
									maxWidth: "360px",
									margin: "4px 0 6px",
									wordBreak: "break-word",
								}}
							>
								<ErrorOutline fontSize="small" className={styles.errorIcon} />
								<span id="txt-login-fail" className={styles.errorMessage} key={item}>
									{item}
								</span>
							</div>
						))}
				</div>
				<ContainerToggle>
					<ButtonToggleScreen type="button" onClick={toggleRecoveryPassword}>
						{getTranslateByKey("information.forgotPassword", false, translatesLocal)}
					</ButtonToggleScreen>
				</ContainerToggle>
				<ButtonLogin
					id="btn-login-signin"
					type="submit"
					disabled={loginForm.submitInProgress}
					paddingCustom={submitInProgress ? "0.6rem 1rem" : undefined}
				>
					{submitInProgress ? (
						<SpinnerLoading size="xsmall" />
					) : (
						<>
							{getTranslateByKey("information.login", false, translatesLocal)}
							<ArrowForward fontSize="small" />
						</>
					)}
				</ButtonLogin>
			</S.ContainerForm>
		</S.Container>
	);
}

function WrappedLoginArea(props: ILoginAreaProps) {
	return (
		<LoginFormProvider>
			<LoginArea {...props} />
		</LoginFormProvider>
	);
}

export default WrappedLoginArea;
